/**
 * Audio-reactive waveform for the RAW→PRESET player.
 * A magenta→violet spectrum behind the player controls: a calm static wave when
 * paused, a live FFT reaction when playing.
 *
 * Safety / perf:
 *  - Static when idle — the rAF loop runs ONLY while audio is playing AND on-screen,
 *    so it costs nothing at load (keeps CWV green).
 *  - AudioContext is created lazily on first play (a user gesture); the media element
 *    is routed source → analyser → destination so playback is never interrupted.
 *  - prefers-reduced-motion → static only, no animation, no audio graph.
 *  - Falls back to a gentle static wave if FFT data isn't available.
 */
(function () {
  if (window.__irWaveInit) return;
  window.__irWaveInit = true;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var BARS = 44;

  function roundRectPath(ctx, x, y, w, h, r) {
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); return; }
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function setup(root) {
    var canvas = root.querySelector('.ir-abp__viz');
    var audio = root.querySelector('audio');
    if (!canvas || !audio || canvas.dataset.viz) return;
    canvas.dataset.viz = '1';

    var ctx = canvas.getContext('2d');
    var analyser = null, freq = null, audioCtx = null, source = null;
    var raf = 0, playing = false, visible = true, t = 0;

    function size() {
      var r = canvas.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(r.width * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function ensureGraph() {
      if (audioCtx) { if (audioCtx.state === 'suspended') audioCtx.resume(); return; }
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      try {
        audioCtx = new AC();
        source = audioCtx.createMediaElementSource(audio);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 128;
        analyser.smoothingTimeConstant = 0.82;
        freq = new Uint8Array(analyser.frequencyBinCount);
        source.connect(analyser);
        analyser.connect(audioCtx.destination);
      } catch (e) { audioCtx = null; analyser = null; }
    }

    function draw() {
      var w = canvas.clientWidth, h = canvas.clientHeight;
      if (!w || !h) return;
      ctx.clearRect(0, 0, w, h);
      var grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, '#ff4ecd');
      grad.addColorStop(1, '#a855f7');
      ctx.fillStyle = grad;

      if (analyser && playing) analyser.getByteFrequencyData(freq);
      var bw = w / BARS;
      for (var i = 0; i < BARS; i++) {
        var v;
        if (analyser && playing) {
          var idx = 1 + Math.floor((i / BARS) * (freq.length - 2));
          v = Math.pow(freq[idx] / 255, 1.35);     // tame the noise floor
          v *= 0.7 + 0.5 * (i / BARS);              // lift highs so it isn't bass-lopsided
        } else {
          v = 0.05 + 0.05 * (0.5 + 0.5 * Math.sin(i * 0.5)); // static gentle wave
        }
        var bh = Math.max(2, v * h * 0.72);
        var bwi = bw * 0.62;
        var x = i * bw + (bw - bwi) / 2;
        var y = (h - bh) / 2;
        ctx.globalAlpha = playing ? 0.92 : 0.7;
        roundRectPath(ctx, x, y, bwi, bh, Math.min(bwi / 2, 3));
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    function loop() { t++; draw(); raf = requestAnimationFrame(loop); }
    function run() { if (raf || REDUCE) return; if (playing && visible) raf = requestAnimationFrame(loop); }
    function halt() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }
    function redraw() { size(); draw(); }

    audio.addEventListener('play', function () {
      playing = true; root.classList.add('is-viz-on');
      ensureGraph();
      if (REDUCE) redraw(); else run();
    });
    function onIdle() { playing = false; root.classList.remove('is-viz-on'); halt(); redraw(); }
    audio.addEventListener('pause', onIdle);
    audio.addEventListener('ended', onIdle);

    if ('ResizeObserver' in window) new ResizeObserver(redraw).observe(canvas);
    else window.addEventListener('resize', redraw);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (!visible) halt(); else if (playing) run();
      }, { threshold: 0.01 }).observe(canvas);
    }

    redraw(); // static idle frame
  }

  function initAll(ctx) { (ctx || document).querySelectorAll('.ir-abp').forEach(setup); }
  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
